import { useCallback, useEffect, useMemo, useState } from "react";
import { RIDE_STATUSES, DERIVED_RIDE_STATUSES, RING_STATE_SOURCE, ringsFor } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #1936 (docs/score/ring-state.md) — the single source of truth for the
// Ring section's rows. One row per ring, in the exact order #1909's
// `ringsFor` establishes (it takes a CLASS LIST, not a show — this card
// does not build a second ring list). A ring's row shape:
//   { ring, status, classId, rideId, class, ride, source, setAt, setBy,
//     classRunning, hasOverride }
// `status`/`classId`/`rideId`/`source`/`setAt`/`setBy` are null on a ring
// with no stored row at all — AC7's "absence of a row already IS the
// automatic state" (docs/score/ring-state.md), never reconstructed as a
// derived guess and mislabeled EXPLICIT.
//
// #340-style single-fetch shape (useClassesNeedingPlacings.js): one
// showId-scoped GET /ride call covers the whole show (never fan out per
// class), one batched POST /ride/status-batch resolves every ride's
// derivedStatus, one GET /ring-state list. classRunning (AC10) mirrors
// useClassesNeedingPlacings' classNeedsPlacings: a class is "finished" only
// once every one of its non-scratched rides has reached FINAL; scratched
// rides don't count either way, and an empty/no-ride class is "running"
// (nothing to call finished yet).
function classIsFinished(rides) {
  if (!rides || rides.length === 0) return false;
  const counted = rides.filter((r) => r.status !== RIDE_STATUSES.SCRATCHED);
  if (counted.length === 0) return false;
  return counted.every((r) => r.derivedStatus === DERIVED_RIDE_STATUSES.FINAL);
}

export function useRingStates(showId) {
  const scoreApi = useScoreApi();
  const [classes, setClasses] = useState([]);
  const [ridesByClassId, setRidesByClassId] = useState({});
  const [ridesById, setRidesById] = useState({});
  const [ringStateByRing, setRingStateByRing] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!showId) {
      setClasses([]);
      setRidesByClassId({});
      setRidesById({});
      setRingStateByRing({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [classList, showRides, ringStates] = await Promise.all([
        scoreApi.classes.listClasses(showId).then(unwrap),
        scoreApi.rides.listRides(undefined, showId).then(unwrap),
        scoreApi.ringState.listRingStates(showId).then(unwrap),
      ]);
      const ids = [...new Set((showRides || []).map((r) => r.id).filter(Boolean))];
      const statusRows = ids.length
        ? unwrap(await scoreApi.rides.batchRideStatus({ rideIds: ids }))
        : [];
      const statusById = Object.fromEntries(statusRows.map((r) => [r.rideId, r.status]));

      const byClass = {};
      const byRideId = {};
      for (const r of showRides || []) {
        const enriched = { ...r, derivedStatus: statusById[r.id] ?? null };
        byRideId[r.id] = enriched;
        if (!r.classId) continue;
        (byClass[r.classId] ??= []).push(enriched);
      }

      const byRing = {};
      for (const row of ringStates || []) {
        if (row?.ring) byRing[row.ring] = row;
      }

      setClasses(classList || []);
      setRidesByClassId(byClass);
      setRidesById(byRideId);
      setRingStateByRing(byRing);
    } catch (err) {
      setClasses([]);
      setRidesByClassId({});
      setRidesById({});
      setRingStateByRing({});
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showId, scoreApi]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const classesById = useMemo(
    () => Object.fromEntries(classes.map((c) => [c.id, c])),
    [classes]
  );

  const rings = useMemo(() => ringsFor(classes), [classes]);

  const rows = useMemo(() => {
    return rings.map((ring) => {
      const stored = ringStateByRing[ring] || null;
      const classId = stored?.classId ?? null;
      const rideId = stored?.rideId ?? null;
      const cls = classId ? classesById[classId] ?? null : null;
      const ride = rideId ? ridesById[rideId] ?? null : null;
      return {
        ring,
        status: stored?.status ?? null,
        classId,
        rideId,
        class: cls,
        ride,
        classRunning: classId ? !classIsFinished(ridesByClassId[classId]) : null,
        source: stored?.source ?? null,
        isExplicit: stored?.source === RING_STATE_SOURCE.EXPLICIT,
        setAt: stored?.setAt ?? null,
        setBy: stored?.setBy ?? null,
        hasOverride: !!stored,
      };
    });
  }, [rings, ringStateByRing, classesById, ridesById, ridesByClassId]);

  return {
    rows,
    classes,
    ridesByClassId,
    loading,
    error,
    refetch,
  };
}

export default useRingStates;
